import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card, Col, Container, Row, Form, Button, Tab, Nav, Badge, Spinner, Dropdown } from "react-bootstrap";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { LotSmartActions } from "../../Actions";
import CustomContentAlert, { scroolToTop } from "../helper";
import moment from "moment";
import DatePicker from "react-datepicker";
import { AuthAPI } from "../../../Auth/requestHandler";
import "react-datepicker/dist/react-datepicker.css";

const DAY_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const DAY_LABEL = { "-1": "Default Rate", ...Object.fromEntries(DAY_OPTIONS.map((d) => [d.value, d.label])) };

const BLANK_RATE = { merchant_id: "", day_of_week: 0, start_time: "00:00", end_time: "23:59", price: "", notes: "" };

const toHHMM = (t) => String(t || "").slice(0, 5);
const toDisplayTime = (t) => { const hhmm = toHHMM(t); return hhmm ? moment(`2000-01-01 ${hhmm}`).format("h:mm A") : ""; };
const inject2359ForDate = (date) => {
  const base = date ? moment(date) : moment();
  return [base.clone().hour(23).minute(59).second(0).millisecond(0).toDate()];
};
const BLANK_SPECIAL = { merchant_id: "", start_utc: null, end_utc: null, price: "", notes: "" };

const getMerchantCardLabel = (merchant) => merchant?.mid || merchant?.device_label || `Merchant #${merchant?.id}`;
const formatRateAmount = (price) => {
  const amount = Number(price);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "\u2014";
};
const resolveActiveRate = (regularRates = [], merchantSpecialRates = []) => {
  const now = moment();

  const activeSpecial = merchantSpecialRates.find((sr) =>
    now.isBetween(moment.utc(sr.start_utc), moment.utc(sr.end_utc), null, "[]")
  );
  if (activeSpecial) {
    return { price: activeSpecial.price, label: activeSpecial.notes || "Special Rate", isSpecial: true };
  }

  const nowDay = now.day();
  const nowTime = now.format("HH:mm");
  const match =
    regularRates.find(
      (r) =>
        Number(r.day_of_week) === nowDay &&
        nowTime >= toHHMM(r.start_time) &&
        nowTime <= toHHMM(r.end_time)
    ) ||
    regularRates.find(
      (r) =>
        Number(r.day_of_week) === -1 &&
        nowTime >= toHHMM(r.start_time) &&
        nowTime <= toHHMM(r.end_time)
    );

  if (match) {
    return { price: match.price, label: match.notes || DAY_LABEL[match.day_of_week], isSpecial: false };
  }
  return null;
};

function LotSmartManage(props) {
  const dispatch = useDispatch();
  const { property_id } = useSelector((s) => s.UserProfileDetails);
  const { merchants, rates, specialRates } = useSelector((s) => s.LotSmartDetails);
  const apiResponse = useSelector((s) => s.APIResponseDetails);

  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [loader, setLoader] = useState(false);
  const [gateLoader, setGateLoader] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [showErrAlert, setShowErrAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [merchantActiveRates, setMerchantActiveRates] = useState({});

  // Rate form
  const [rateForm, setRateForm] = useState(BLANK_RATE);
  const [editingRateId, setEditingRateId] = useState(null);
  const [showRateForm, setShowRateForm] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);

  // Special Rate form
  const [specialForm, setSpecialForm] = useState(BLANK_SPECIAL);
  const [editingSpecialId, setEditingSpecialId] = useState(null);
  const [showSpecialForm, setShowSpecialForm] = useState(false);
  const [flashForm, setFlashForm] = useState({ hours: "1", price: "", notes: "FlashEvent" });
  const [flashDurationUnit, setFlashDurationUnit] = useState("hours");

  // Default rate inline editor
  const [defaultForm, setDefaultForm] = useState({ price: "", notes: "" });
  const [editingDefault, setEditingDefault] = useState(false);

  useEffect(() => {
    if (property_id) {
      dispatch(LotSmartActions.getLotSmartMerchants(property_id));
    }
  }, [dispatch, property_id]);

  useEffect(() => {
    if (!merchants.length) {
      setMerchantActiveRates({});
      return;
    }

    let cancelled = false;

    const loadMerchantActiveRates = async () => {
      const entries = await Promise.all(
        merchants.map(async (merchant) => {
          const [ratesResp, specialResp] = await Promise.all([
            AuthAPI("domain", "lotsmart/rates/list", "POST", { merchant_id: merchant.id }),
            AuthAPI("domain", "lotsmart/special-rates/list", "POST", { merchant_id: merchant.id }),
          ]);

          const resolvedRate = resolveActiveRate(
            ratesResp?.success ? ratesResp.data : [],
            specialResp?.success ? specialResp.data : []
          );

          return [merchant.id, resolvedRate];
        })
      );

      if (!cancelled) {
        setMerchantActiveRates(Object.fromEntries(entries));
      }
    };

    loadMerchantActiveRates();

    return () => {
      cancelled = true;
    };
  }, [merchants]);

  const loadRates = useCallback(
    async (merchant) => {
      if (!merchant) return;
      setLoader(true);
      await Promise.all([
        dispatch(LotSmartActions.getLotSmartRates(merchant.id)),
        dispatch(LotSmartActions.getLotSmartSpecialRates(merchant.id)),
      ]);
      setLoader(false);
    },
    [dispatch]
  );

  const handleMerchantSelect = (m) => {
    setSelectedMerchant(m);
    setShowRateForm(false);
    setShowSpecialForm(false);
    loadRates(m);
  };

  const triggerAlert = (msg, isErr = false) => {
    setAlertMsg(msg);
    if (isErr) {
      setShowErrAlert(false);
      setTimeout(() => setShowErrAlert(true), 0);
    } else {
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
    }
    scroolToTop();
  };

  // ─── Gate Control ─────────────────────────────────────────────────────────

  const handleOpenGate = async (merchant) => {
    setGateLoader((prev) => ({ ...prev, [merchant.id]: true }));
    const res = await dispatch(LotSmartActions.openLotSmartGate(merchant.id, property_id));
    setGateLoader((prev) => ({ ...prev, [merchant.id]: false }));
    if (res?.success) {
      triggerAlert(`Gate opened for ${merchant.device_label || merchant.serial_number}`);
    } else {
      triggerAlert(res?.message || "Gate open failed", true);
    }
  };

  // ─── Rate CRUD ────────────────────────────────────────────────────────────

  const openNewRateForm = () => {
    setRateForm({ ...BLANK_RATE, merchant_id: selectedMerchant.id });
    setSelectedDays([]);
    setEditingRateId(null);
    setShowRateForm(true);
  };

  const openEditRateForm = (row) => {
    const dayValue = Number(row.day_of_week);
    setSelectedDays([DAY_OPTIONS.find((d) => d.value === dayValue)].filter(Boolean));
    setRateForm({
      merchant_id: row.merchant_id,
      day_of_week: dayValue,
      start_time: row.start_time,
      end_time: row.end_time,
      price: row.price,
      notes: row.notes || "",
    });
    setEditingRateId(row.rate_id);
    setShowRateForm(true);
  };

  const handleRateSave = async () => {
    if (!selectedDays.length) {
      triggerAlert("Please select at least one day.", true);
      return;
    }

    if (toHHMM(rateForm.end_time) <= toHHMM(rateForm.start_time)) {
      triggerAlert("End time must be later than start time.", true);
      return;
    }

    // Exclude the default rate and the rate being edited from conflict checks
    const regularRates = rates.filter((r) => Number(r.day_of_week) !== -1);
    const otherRates = regularRates.filter((r) => r.rate_id !== editingRateId);
    // Normalize to HH:mm — DB may store "HH:mm:ss" while the form produces "HH:mm"
    const newStart = toHHMM(rateForm.start_time);
    const newEnd = toHHMM(rateForm.end_time);

    for (const day of selectedDays) {
      const conflicting = otherRates.find(
        (r) =>
          Number(r.day_of_week) === day.value &&
          newStart < toHHMM(r.end_time) &&
          newEnd > toHHMM(r.start_time)
      );
      if (conflicting) {
        triggerAlert(
          `A rate for ${day.label} (${toDisplayTime(conflicting.start_time)}–${toDisplayTime(conflicting.end_time)}) already covers part of the selected time range.`,
          true
        );
        return;
      }
    }

    const basePayload = { ...rateForm, merchant_id: selectedMerchant.id };

    if (editingRateId) {
      const payload = { ...basePayload, day_of_week: selectedDays[0]?.value };
      const res = await dispatch(LotSmartActions.updateLotSmartRate(editingRateId, payload));
      if (res?.success) {
        triggerAlert("Rate updated");
        setShowRateForm(false);
        await dispatch(LotSmartActions.getLotSmartRates(selectedMerchant.id));
      } else {
        triggerAlert(res?.message || "Save failed", true);
      }
    } else {
      // Create one rate record per selected day
      const results = await Promise.all(
        selectedDays.map((day) =>
          dispatch(LotSmartActions.createLotSmartRate({ ...basePayload, day_of_week: day.value }))
        )
      );
      const failed = results.filter((r) => !r?.success);
      if (failed.length === 0) {
        triggerAlert(results.length > 1 ? `${results.length} rates created` : "Rate created");
        setShowRateForm(false);
      } else {
        triggerAlert(failed[0]?.message || "Some rates could not be saved.", true);
      }
      await dispatch(LotSmartActions.getLotSmartRates(selectedMerchant.id));
    }
  };

  const handleRateDelete = async (rate_id) => {
    if (!window.confirm("Delete this rate?")) return;
    const res = await dispatch(LotSmartActions.deleteLotSmartRate(rate_id));
    if (res?.success) {
      triggerAlert("Rate deleted");
      await dispatch(LotSmartActions.getLotSmartRates(selectedMerchant.id));
    } else {
      triggerAlert(res?.message || "Delete failed", true);
    }
  };

  // ─── Special Rate CRUD ────────────────────────────────────────────────────

  const openNewSpecialForm = () => {
    setSpecialForm({ ...BLANK_SPECIAL, merchant_id: selectedMerchant.id });
    setEditingSpecialId(null);
    setShowSpecialForm(true);
  };

  const openEditSpecialForm = (row) => {
    setSpecialForm({
      merchant_id: row.merchant_id,
      start_utc: row.start_utc ? moment(row.start_utc).toDate() : null,
      end_utc: row.end_utc ? moment(row.end_utc).toDate() : null,
      price: row.price,
      notes: row.notes || "",
    });
    setEditingSpecialId(row.special_id);
    setShowSpecialForm(true);
  };

  const handleSpecialSave = async () => {
    if (!specialForm.start_utc || !specialForm.end_utc) {
      triggerAlert("Please select both a start and end date/time.", true);
      return;
    }
    if (specialForm.end_utc <= specialForm.start_utc) {
      triggerAlert("End date/time must be after start date/time.", true);
      return;
    }

    // Conflict check — touching boundaries (end == start) are allowed
    const newStart = specialForm.start_utc.getTime();
    const newEnd = specialForm.end_utc.getTime();
    const otherSpecials = specialRates.filter((sr) => sr.special_id !== editingSpecialId);
    const conflicting = otherSpecials.find((sr) => {
      const exStart = moment(sr.start_utc).valueOf();
      const exEnd = moment(sr.end_utc).valueOf();
      return newStart < exEnd && newEnd > exStart;
    });
    if (conflicting) {
      triggerAlert(
        `This event rate overlaps with an existing event rate (${moment(conflicting.start_utc).format("MM/DD/YYYY h:mm A")} – ${moment(conflicting.end_utc).format("MM/DD/YYYY h:mm A")}).`,
        true
      );
      return;
    }

    const payload = {
      ...specialForm,
      merchant_id: selectedMerchant.id,
      start_utc: moment(specialForm.start_utc).toISOString(),
      end_utc: moment(specialForm.end_utc).toISOString(),
    };
    let res;
    if (editingSpecialId) {
      res = await dispatch(LotSmartActions.updateLotSmartSpecialRate(editingSpecialId, payload));
    } else {
      res = await dispatch(LotSmartActions.createLotSmartSpecialRate(payload));
    }
    if (res?.success) {
      triggerAlert(editingSpecialId ? "Special rate updated" : "Special rate created");
      setShowSpecialForm(false);
      await dispatch(LotSmartActions.getLotSmartSpecialRates(selectedMerchant.id));
    } else {
      triggerAlert(res?.message || "Save failed", true);
    }
  };

  const handleSpecialDelete = async (special_id) => {
    if (!window.confirm("Delete this special rate?")) return;
    const res = await dispatch(LotSmartActions.deleteLotSmartSpecialRate(special_id));
    if (res?.success) {
      triggerAlert("Special rate deleted");
      await dispatch(LotSmartActions.getLotSmartSpecialRates(selectedMerchant.id));
    } else {
      triggerAlert(res?.message || "Delete failed", true);
    }
  };

  const isSpecialRateActive = (row) => moment().isBetween(moment(row.start_utc), moment(row.end_utc), null, "[]");

  const handleSpecialEndNow = async (row) => {
    if (!window.confirm("End this active rate now?")) return;

    const now = moment();
    if (now.valueOf() <= moment(row.start_utc).valueOf()) {
      triggerAlert("This rate has not started yet, so it cannot be ended now.", true);
      return;
    }

    const payload = {
      start_utc: moment(row.start_utc).toISOString(),
      end_utc: now.toISOString(),
      price: row.price,
      notes: row.notes || "",
    };

    const res = await dispatch(LotSmartActions.updateLotSmartSpecialRate(row.special_id, payload));
    if (res?.success) {
      triggerAlert((row.notes || "").toLowerCase().includes("flash") ? "Flash event ended" : "Special rate ended");
      await dispatch(LotSmartActions.getLotSmartSpecialRates(selectedMerchant.id));
    } else {
      triggerAlert(res?.message || "Unable to end the active rate.", true);
    }
  };

  const handleFlashSaleSave = async () => {
    const durationValue = Number(flashForm.hours);
    const durationHours = flashDurationUnit === "minutes" ? durationValue / 60 : durationValue;
    if (!flashForm.price || Number(flashForm.price) < 0) {
      triggerAlert("Please enter a valid flash sale price.", true);
      return;
    }
    if (!durationValue || durationValue <= 0) {
      triggerAlert(`Please enter a valid flash sale duration in ${flashDurationUnit}.`, true);
      return;
    }

    const start = moment();
    const end = start.clone().add(durationHours, "hours");

    const conflicting = specialRates.find((sr) => {
      const exStart = moment(sr.start_utc).valueOf();
      const exEnd = moment(sr.end_utc).valueOf();
      return start.valueOf() < exEnd && end.valueOf() > exStart;
    });
    if (conflicting) {
      triggerAlert(
        `A flash event has already started (${moment(conflicting.start_utc).format("MM/DD/YYYY h:mm A")} – ${moment(conflicting.end_utc).format("MM/DD/YYYY h:mm A")}). Please wait until it ends before starting another one.`,
        true
      );
      return;
    }

    const payload = {
      merchant_id: selectedMerchant.id,
      start_utc: start.toISOString(),
      end_utc: end.toISOString(),
      price: flashForm.price,
      notes: flashForm.notes || "FlashEvent",
    };

    const res = await dispatch(LotSmartActions.createLotSmartSpecialRate(payload));
    if (res?.success) {
      triggerAlert("Flash event created");
      setFlashForm((p) => ({ ...p, price: "" }));
      await dispatch(LotSmartActions.getLotSmartSpecialRates(selectedMerchant.id));
    } else {
      triggerAlert(res?.message || "Flash event create failed", true);
    }
  };

  // ─── Active rate & default rate ───────────────────────────────────────────

  const activeRate = useMemo(() => {
    if (!selectedMerchant) return null;
    return resolveActiveRate(rates, specialRates);
  }, [rates, selectedMerchant, specialRates]);

  useEffect(() => {
    if (!selectedMerchant) return;
    setMerchantActiveRates((prev) => ({ ...prev, [selectedMerchant.id]: activeRate }));
  }, [activeRate, selectedMerchant]);

  const defaultRate = useMemo(
    () => rates.find((r) => Number(r.day_of_week) === -1) || null,
    [rates]
  );

  const openDefaultRateEdit = () => {
    setDefaultForm({ price: defaultRate ? defaultRate.price : "", notes: defaultRate ? defaultRate.notes || "" : "" });
    setEditingDefault(true);
  };

  const handleDefaultRateSave = async () => {
    // Guard: only one default rate allowed per device
    const existingDefaults = rates.filter((r) => Number(r.day_of_week) === -1);
    if (!defaultRate && existingDefaults.length > 0) {
      triggerAlert("A default rate already exists for this device. Only one default rate is allowed.", true);
      return;
    }
    const payload = {
      merchant_id: selectedMerchant.id,
      day_of_week: -1,
      start_time: "00:00",
      end_time: "23:59",
      price: defaultForm.price,
      notes: defaultForm.notes,
    };
    const res = defaultRate
      ? await dispatch(LotSmartActions.updateLotSmartRate(defaultRate.rate_id, payload))
      : await dispatch(LotSmartActions.createLotSmartRate(payload));
    if (res?.success) {
      triggerAlert("Default rate saved");
      setEditingDefault(false);
      await dispatch(LotSmartActions.getLotSmartRates(selectedMerchant.id));
    } else {
      triggerAlert(res?.message || "Save failed", true);
    }
  };

  // ─── Table columns ────────────────────────────────────────────────────────

  const tableOverflowStyles = {
    table: { style: { minWidth: "760px" } },
    headCells: {
      style: {
        whiteSpace: "normal",
        overflow: "visible",
        textOverflow: "clip",
        lineHeight: "1.2",
      },
    },
    responsiveWrapper: {
      style: {
        overflowX: "auto",
        overflowY: "visible",
        WebkitOverflowScrolling: "touch",
      },
    },
  };

  const rateColumns = [
    { name: "Day", selector: (r) => DAY_LABEL[r.day_of_week] ?? r.day_of_week, sortable: true, minWidth: "110px" },
    { name: "Start Time", selector: (r) => toDisplayTime(r.start_time), sortable: true, minWidth: "120px" },
    { name: "End Time", selector: (r) => toDisplayTime(r.end_time), sortable: true, minWidth: "120px" },
    { name: "Price ($)", selector: (r) => `$${parseFloat(r.price).toFixed(2)}`, sortable: true, minWidth: "105px" },
    { name: "Notes", selector: (r) => r.notes || "-", minWidth: "160px", grow: 2, wrap: true },
    {
      name: "Actions",
      minWidth: "130px",
      cell: (r) => (
        <Dropdown>
          <Dropdown.Toggle variant="Primary" className="btn-xs" id="dropdown-rate-actions">
            Action
          </Dropdown.Toggle>
          <Dropdown.Menu strategy="fixed">
            <Dropdown.Item onClick={() => openEditRateForm(r)}>Edit Rate</Dropdown.Item>
            <Dropdown.Item onClick={() => handleRateDelete(r.rate_id)}>Delete Rate</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ),
    },
  ];

  const specialColumns = [
    { name: "Start Date ", selector: (r) => moment(r.start_utc).format("YYYY-MM-DD HH:mm"), sortable: true, minWidth: "150px" },
    { name: "End Date", selector: (r) => moment(r.end_utc).format("YYYY-MM-DD HH:mm"), sortable: true, minWidth: "150px" },
    { name: "Price ($)", selector: (r) => `$${parseFloat(r.price).toFixed(2)}`, sortable: true, minWidth: "105px" },
    { name: "Notes", selector: (r) => r.notes || "-", minWidth: "160px", grow: 2, wrap: true },
    {
      name: "Actions",
      minWidth: "130px",
      cell: (r) => (
        <Dropdown>
          <Dropdown.Toggle variant="Primary" className="btn-xs" id="dropdown-special-actions">
            Action
          </Dropdown.Toggle>
          <Dropdown.Menu strategy="fixed">
            {isSpecialRateActive(r) && <Dropdown.Item onClick={() => handleSpecialEndNow(r)}>End Now</Dropdown.Item>}
            <Dropdown.Item onClick={() => openEditSpecialForm(r)}>Edit Rate</Dropdown.Item>
            <Dropdown.Item onClick={() => handleSpecialDelete(r.special_id)}>Delete Rate</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ),
    },
  ];

  return (
    <Container fluid>
      <Row>
        <Col md="12">
          <Card className="mb-0">
            <Card.Header>
              <div className="d-flex align-items-sm-center align-items-start justify-content-between">
                <div>
                  <Card.Title as="h4">LotSmart</Card.Title>
                  <small className="text-muted">Manage Rates &amp; Gate Control</small>
                </div>
              </div>
            </Card.Header>
            <Card.Body>

      {showAlert && (
        <CustomContentAlert
          delay={4000}
          className="toast-success"
          message={alertMsg}
          onHide={() => setShowAlert(false)}
        />
      )}
      {showErrAlert && (
        <CustomContentAlert
          delay={4000}
          className="toast-error"
          message={alertMsg}
          onHide={() => setShowErrAlert(false)}
        />
      )}

      {/* Merchant list */}
      <Row className="mb-3">
        {merchants.length === 0 && (
          <Col>
            <Card body className="text-muted">No LotSmart devices found for this property.</Card>
          </Col>
        )}
        {merchants.map((m) => (
          <Col xs={12} md={6} lg={4} key={m.id} className="mb-2">
            <Card
              className={`h-100 ${selectedMerchant?.id === m.id ? "border-primary" : ""}`}
              style={{ cursor: "pointer" }}
              onClick={() => handleMerchantSelect(m)}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <Card.Title className="mb-1">{getMerchantCardLabel(m)}</Card.Title>
                    <small className="text-muted d-block">S/N: {m.serial_number || "—"}</small>
                    <small className="text-muted d-block">MID: {m.mid} | TID: {m.tid}</small>
                    <small className="text-muted d-block mt-1">
                      Active Rate: {merchantActiveRates[m.id] ? `${formatRateAmount(merchantActiveRates[m.id].price)} (${merchantActiveRates[m.id].label || "Current"})` : "—"}
                    </small>
                  </div>
                  <Badge bg={m.active ? "success" : "secondary"}>{m.active ? "Active" : "Inactive"}</Badge>
                </div>
              </Card.Body>
              <Card.Footer className="d-flex justify-content-end">
                <Button
                  size="sm"
                  variant="success"
                  disabled={!m.active || gateLoader[m.id]}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenGate(m);
                  }}
                >
                  {gateLoader[m.id] ? <Spinner animation="border" size="sm" /> : "Open Gate"}
                </Button>
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Rates management */}
      {selectedMerchant && (
        <Card>
          <Card.Header>
            <strong>{getMerchantCardLabel(selectedMerchant)}</strong> — Rate Management
          </Card.Header>
          <Card.Body>
            {loader ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <>
                {/* ── Active Rate Banner + Default Rate ── */}
                <Row className="mb-3">
                  <Col xs={12} md={6} className="mb-2">
                    <Card style={{ borderColor: activeRate ? (activeRate.isSpecial ? "#ffc107" : "#28a745") : "#6c757d" }}>
                      <Card.Body className="py-3">
                        <small className="text-uppercase text-muted d-block mb-1" style={{ fontSize: "0.7rem", letterSpacing: "0.06em" }}>Current Active Rate</small>
                        <div className="d-flex align-items-center">
                          <span style={{ fontSize: "2.25rem", fontWeight: 700, lineHeight: 1, marginRight: "0.75rem", color: activeRate ? (activeRate.isSpecial ? "#856404" : "#155724") : "#6c757d" }}>
                            {activeRate ? formatRateAmount(activeRate.price) : "—"}
                          </span>
                          <div>
                            <Badge variant={activeRate ? (activeRate.isSpecial ? "warning" : "success") : "secondary"}>
                              {activeRate ? activeRate.label : "No matching rate right now"}
                            </Badge>
                            {activeRate?.isSpecial && (
                              <small className="d-block text-muted mt-1">Special rate override is active</small>
                            )}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12} md={6} className="mb-2">
                    <Card>
                      <Card.Body className="py-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div style={{ flex: 1 }}>
                            <small className="text-uppercase text-muted d-block mb-1" style={{ fontSize: "0.7rem", letterSpacing: "0.06em" }}>Default Rate <span className="text-lowercase font-weight-normal">(Rate Used When no Day or Event Rate Set)</span></small>
                            {!editingDefault ? (
                              <div className="d-flex align-items-center">
                                <span style={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1, marginRight: "0.5rem" }}>
                                  {defaultRate ? `$${parseFloat(defaultRate.price).toFixed(2)}` : <span className="text-muted" style={{ fontSize: "0.95rem" }}>Not set</span>}
                                </span>
                                {defaultRate?.notes && <small className="text-muted">{defaultRate.notes}</small>}
                              </div>
                            ) : (
                              <div className="d-flex flex-wrap align-items-center mt-2">
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="Price ($)"
                                  style={{ width: "100px", marginRight: "0.4rem" }}
                                  value={defaultForm.price}
                                  onChange={(e) => setDefaultForm((p) => ({ ...p, price: e.target.value }))}
                                />
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  placeholder="Notes (optional)"
                                  style={{ width: "150px", marginRight: "0.4rem" }}
                                  value={defaultForm.notes}
                                  onChange={(e) => setDefaultForm((p) => ({ ...p, notes: e.target.value }))}
                                />
                                <Button size="sm" className="mr-1" onClick={handleDefaultRateSave}>Save</Button>
                                <Button size="sm" variant="secondary" onClick={() => setEditingDefault(false)}>Cancel</Button>
                              </div>
                            )}
                          </div>
                          {!editingDefault && (
                            <Button size="sm" variant="outline-secondary" onClick={openDefaultRateEdit}>
                              {defaultRate ? "Edit" : "Set Default"}
                            </Button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Tab.Container defaultActiveKey="regular">
                  <Nav variant="tabs" className="mb-3">
                    <Nav.Item>
                      <Nav.Link eventKey="regular">Regular Rates</Nav.Link>
                    </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="special">Event Rates</Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  {/* ── Regular Rates ── */}
                  <Tab.Pane eventKey="regular">
                    <div className="d-flex justify-content-end mb-2">
                      <Button size="sm" onClick={openNewRateForm}>
                        + Add Rate
                      </Button>
                    </div>

                    {showRateForm && (
                      <Card className="mb-3 border-primary">
                        <Card.Header>{editingRateId ? "Edit Rate" : "New Rate"}</Card.Header>
                        <Card.Body>
                          <Row>
                            <Col xs={12} md={4} className="mb-2">
                              <Form.Label>Day(s) of Week</Form.Label>
                              <Select
                                isMulti
                                closeMenuOnSelect={false}
                                options={DAY_OPTIONS}
                                placeholder="Select day(s)"
                                value={selectedDays}
                                onChange={(opts) => setSelectedDays(opts || [])}
                              />
                            </Col>
                            <Col xs={6} md={2} className="mb-2">
                              <Form.Label>Start Time</Form.Label>
                              <DatePicker
                                selected={rateForm.start_time ? moment(`2000-01-01 ${rateForm.start_time}`).toDate() : null}
                                onChange={(date) => date && setRateForm((p) => ({ ...p, start_time: moment(date).format("HH:mm") }))}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="h:mm aa"
                                timeFormat="h:mm aa"
                                injectTimes={[new Date(2000, 0, 1, 23, 59)]}
                                className="form-control"
                                placeholderText="Select time"
                                popperPlacement="bottom-start"
                                wrapperClassName="w-100"
                              />
                            </Col>
                            <Col xs={6} md={2} className="mb-2">
                              <Form.Label>End Time</Form.Label>
                              <DatePicker
                                selected={rateForm.end_time ? moment(`2000-01-01 ${rateForm.end_time}`).toDate() : null}
                                onChange={(date) => date && setRateForm((p) => ({ ...p, end_time: moment(date).format("HH:mm") }))}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="h:mm aa"
                                timeFormat="h:mm aa"
                                injectTimes={[new Date(2000, 0, 1, 23, 59)]}
                                className="form-control"
                                placeholderText="Select time"
                                popperPlacement="bottom-start"
                                wrapperClassName="w-100"
                              />
                            </Col>
                            <Col xs={6} md={2} className="mb-2">
                              <Form.Label>Price ($)</Form.Label>
                              <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                value={rateForm.price}
                                onChange={(e) => setRateForm((p) => ({ ...p, price: e.target.value }))}
                              />
                            </Col>
                            <Col xs={6} md={2} className="mb-2">
                              <Form.Label>Notes</Form.Label>
                              <Form.Control
                                type="text"
                                value={rateForm.notes}
                                onChange={(e) => setRateForm((p) => ({ ...p, notes: e.target.value }))}
                              />
                            </Col>
                          </Row>
                          <div className="d-flex gap-2">
                            <Button size="sm" onClick={handleRateSave}>Save</Button>
                            <Button size="sm" variant="secondary" onClick={() => setShowRateForm(false)}>Cancel</Button>
                          </div>
                        </Card.Body>
                      </Card>
                    )}

                    <DataTable
                      columns={rateColumns}
                      data={rates.filter((r) => Number(r.day_of_week) !== -1)}
                      pagination
                      noDataComponent="No rates configured"
                      dense
                      customStyles={tableOverflowStyles}
                    />
                  </Tab.Pane>

                  {/* ── Special Rates ── */}
                  <Tab.Pane eventKey="special">
                    <Card className="mb-3 border-warning">
                      <Card.Body className="py-3">
                        <div className="overflow-auto pb-1">
                        <div className="d-flex flex-nowrap flex-md-wrap align-items-end">
                          <div className="mr-2 mb-2" style={{ width: "170px" }}>
                            <Form.Label className="mb-1">Flash Event</Form.Label>
                            <small className="text-muted d-block">Starts immediately.</small>
                          </div>
                          <div className="mr-2 mb-2" style={{ width: "110px" }}>
                            <Form.Label className="mb-1">Active For</Form.Label>
                            <Form.Control
                              type="number"
                              min={flashDurationUnit === "minutes" ? "1" : "0.1"}
                              step={flashDurationUnit === "minutes" ? "1" : "0.1"}
                              value={flashForm.hours}
                              
                              onChange={(e) => setFlashForm((p) => ({ ...p, hours: e.target.value }))}
                            />
                          </div>
                          <div className="mr-2 mb-2" style={{ width: "100px" }}>
                            <Form.Label className="mb-1">Duration</Form.Label>
                            <Form.Control
                              as="select"
                              value={flashDurationUnit}
                              onChange={(e) => setFlashDurationUnit(e.target.value)}
                            >
                              <option value="hours">Hours</option>
                              <option value="minutes">Minutes</option>
                            </Form.Control>
                          </div>
                          <div className="mr-2 mb-2" style={{ width: "130px" }}>
                            <Form.Label className="mb-1">Price ($)</Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={flashForm.price}
                              onChange={(e) => setFlashForm((p) => ({ ...p, price: e.target.value }))}
                            />
                          </div>
                          <div className="mr-2 mb-2" style={{ width: "170px" }}>
                            <Form.Label className="mb-1">Notes</Form.Label>
                            <Form.Control
                              type="text"
                              value={flashForm.notes}
                              onChange={(e) => setFlashForm((p) => ({ ...p, notes: e.target.value }))}
                            />
                          </div>
                          <div className="mb-2 pt-4">
                            <Button size="sm" variant="warning" onClick={handleFlashSaleSave}>Start</Button>
                          </div>
                        </div>
                        </div>
                      </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end mb-2">
                      <Button size="sm" onClick={openNewSpecialForm}>
                        + Add Special Rate
                      </Button>
                    </div>

                    {showSpecialForm && (
                      <Card className="mb-3 border-primary">
                        <Card.Header>{editingSpecialId ? "Edit Special Rate" : "New Special Rate"}</Card.Header>
                        <Card.Body>
                          <Row>
                            <Col xs={12} md={3} className="mb-2">
                              <Form.Label>Start Date &amp; Time</Form.Label>
                              <DatePicker
                                className="form-control"
                                placeholderText="Select Start Date"
                                selected={specialForm.start_utc}
                                onChange={(date) => setSpecialForm((p) => ({ ...p, start_utc: date }))}
                                selectsStart
                                startDate={specialForm.start_utc}
                                endDate={specialForm.end_utc}
                                dateFormat="MM/dd/yyyy h:mm aa"
                                showTimeSelect
                                timeIntervals={15}
                                injectTimes={inject2359ForDate(specialForm.start_utc)}
                                wrapperClassName="w-100"
                              />
                            </Col>
                            <Col xs={12} md={3} className="mb-2">
                              <Form.Label>End Date &amp; Time</Form.Label>
                              <DatePicker
                                className="form-control"
                                placeholderText="Select End Date"
                                selected={specialForm.end_utc}
                                onChange={(date) => setSpecialForm((p) => ({ ...p, end_utc: date }))}
                                selectsEnd
                                startDate={specialForm.start_utc}
                                endDate={specialForm.end_utc}
                                minDate={specialForm.start_utc}
                                dateFormat="MM/dd/yyyy h:mm aa"
                                showTimeSelect
                                timeIntervals={15}
                                injectTimes={inject2359ForDate(specialForm.end_utc || specialForm.start_utc)}
                                wrapperClassName="w-100"
                              />
                            </Col>
                            <Col xs={6} md={3} className="mb-2">
                              <Form.Label>Price ($)</Form.Label>
                              <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                value={specialForm.price}
                                onChange={(e) => setSpecialForm((p) => ({ ...p, price: e.target.value }))}
                              />
                            </Col>
                            <Col xs={6} md={3} className="mb-2">
                              <Form.Label>Notes</Form.Label>
                              <Form.Control
                                type="text"
                                value={specialForm.notes}
                                onChange={(e) => setSpecialForm((p) => ({ ...p, notes: e.target.value }))}
                              />
                            </Col>
                          </Row>
                          <div className="d-flex gap-2">
                            <Button size="sm" onClick={handleSpecialSave}>Save</Button>
                            <Button size="sm" variant="secondary" onClick={() => setShowSpecialForm(false)}>Cancel</Button>
                          </div>
                        </Card.Body>
                      </Card>
                    )}

                    <DataTable
                      columns={specialColumns}
                      data={specialRates}
                      pagination
                      noDataComponent="No special rates configured"
                      dense
                      customStyles={tableOverflowStyles}
                    />
                  </Tab.Pane>
                </Tab.Content>
                </Tab.Container>
              </>
            )}
          </Card.Body>
        </Card>
      )}

            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default LotSmartManage;
